<template>
  <div class="alert-container">
    <component
      :is="isLegacyAlert ? LegacyAlert : SeparateAlert"
      v-if="hasAlertsToDisplay"
      :active-alerts="activeAlerts"
      :hide-alerts="hideAlerts"
      @close="handleAlertClose"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import isEmpty from 'lodash/isEmpty';
import SeparateAlert from './SeparateAlert.vue';
import LegacyAlert from './LegacyAlert.vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useAlertBannerStore } from '../../../stores/alertBannerStore.js';

defineOptions({ name: 'AlertContainer' });

defineProps({
  hideAlerts: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['keyboard-close']);

const mainStore = useMainStore();
const route = useRoute();
const alertBannerStore = useAlertBannerStore();

const dismissedAlertsInSessionStorage = ref({});

const featureFlags = computed(() => mainStore.featureFlags);
const secondaryOrgId = computed(() => mainStore.secondaryOrgId);
const alertBannerConfig = computed(() => alertBannerStore.alertBannerConfig);
const alertBannerSeparate = computed(() => alertBannerStore.alertBannerSeparate);

const isLegacyAlert = computed(() => !featureFlags.value.cms_separate_alert_banner_enabled);

const publishedAlerts = computed(() => {
  if (isLegacyAlert.value) return alertBannerConfig.value.enabled ? [alertBannerConfig.value] : [];

  return Array.isArray(alertBannerSeparate.value)
    ? alertBannerSeparate.value.filter((banner) => banner.status === 'published').reverse()
    : [];
});

const orgSpecificAlerts = computed(() => dismissedAlertsInSessionStorage.value[secondaryOrgId.value] || {});

const hasNoDismissedAlerts = computed(
  () => isEmpty(dismissedAlertsInSessionStorage.value) || isEmpty(orgSpecificAlerts.value),
);

const dismissedAlertIds = computed(() =>
  Object.entries(orgSpecificAlerts.value).reduce((acc, [key, value]) => {
    if (value === 'hidden') {
      acc.add(key);
    }
    return acc;
  }, new Set()),
);

const isHomepageOrPreview = computed(() =>
  ['index', 'preview-token-id', 'alias-index', 'alias-preview-token-id'].includes(route.name),
);

function filterLegacyAlerts(activeAlertArray) {
  return activeAlertArray.filter((alert) => {
    const isLegacy = alert.enabled && !alert.secondary_organization_id;
    const shouldDisplay = isHomepageOrPreview.value || alert.display_on_all_pages;
    return isLegacy && shouldDisplay;
  });
}

function filterSeparateAlerts(activeAlertArray) {
  return isHomepageOrPreview.value
    ? activeAlertArray
    : activeAlertArray.filter((alert) => alert.display_location === 'all_pages');
}

function filterAlertsToDisplay(activeAlertArray) {
  return isLegacyAlert.value ? filterLegacyAlerts(activeAlertArray) : filterSeparateAlerts(activeAlertArray);
}

const activeAlerts = computed(() => {
  if (!publishedAlerts.value.length || route.name === 'page-preview-slug') return [];
  if (hasNoDismissedAlerts.value) return filterAlertsToDisplay(publishedAlerts.value);

  const nonDismissedAlerts = publishedAlerts.value.filter((item) => {
    const itemId = isLegacyAlert.value ? 'legacy' : item.id.toString();
    return !dismissedAlertIds.value.has(itemId);
  });

  return filterAlertsToDisplay(nonDismissedAlerts);
});

const hasAlertsToDisplay = computed(() => !isEmpty(activeAlerts.value));

function getSessionStorage() {
  if (!import.meta.client) return {};

  try {
    const raw = window.sessionStorage.getItem('thrillshare_alert_banner');
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('Error reading from sessionStorage:', error);
    return {};
  }
}

onBeforeMount(() => {
  dismissedAlertsInSessionStorage.value = getSessionStorage();
});

watch(
  activeAlerts,
  () => {
    const hasActiveModalAlerts = activeAlerts.value.some((alert) => alert.style === 'light_box');
    alertBannerStore.setAlertModalOpen(hasActiveModalAlerts);
  },
  { immediate: true },
);

function updateDismissedAlerts(alertId) {
  const orgBanners = {
    ...orgSpecificAlerts.value,
    [alertId]: 'hidden',
  };
  const objectToStore = {
    ...dismissedAlertsInSessionStorage.value,
    [secondaryOrgId.value]: orgBanners,
  };
  window.sessionStorage.setItem('thrillshare_alert_banner', JSON.stringify(objectToStore));
  dismissedAlertsInSessionStorage.value = objectToStore;
}

function handleAlertClose(alertObject) {
  const { alertId, closedByKeyboard } = alertObject;
  if (closedByKeyboard) {
    void nextTick(() => emit('keyboard-close'));
  }
  updateDismissedAlerts(alertId);
}

defineExpose({
  dismissedAlertsInSessionStorage,
  updateDismissedAlerts,
  handleAlertClose,
  activeAlerts,
  hasAlertsToDisplay,
  isLegacyAlert,
});
</script>
